/**
 * 좁힌 표의 정렬 단추 셋 — 표 위쪽 가장자리의 왼쪽 위·가운데 위·오른쪽 위에 뜬다(오너 결정 2026-10-06 B안).
 * 단추 자리가 곧 뜻이다(왼쪽 위 = 왼쪽 정렬). 값은 표 노드의 정렬 칸(format) — utils/tableWidth.ts 「표 정렬」.
 *
 * 언제 뜨나: 편집 모드에서 커서가 **이 편집기의** 표 안에 있고, 그 표가 담는 칸보다 좁을 때만.
 *   - 꽉 찬 표는 정렬해도 보이는 변화가 없어 띄우지 않는다(누르면 아무 일 없는 단추가 된다). 좁히면 그때 뜬다.
 *   - 본문·풀이 두 편집기가 함께 있어도 Lexical 은 떠난 편집기의 선택을 쥐고 있다 → 실제 커서(DOM 선택)로 가른다.
 * 어디에: 표 위에 단추 자리(24 + 4)가 편집기 글자 칸 안에 없으면(표가 맨 위에 있으면) 표 아래로 내린다 —
 *   위로 올리면 도구 모음을 덮는다. 아래에서는 마지막 줄의 높이 손잡이(칸 아래 10px 띠, TableCellResizer)를
 *   피해 12px 떨어뜨린다. 너무 좁은 표(단추 셋이 안 들어가는 폭)는 셋을 가운데에 나란히 둔다.
 *   표를 감싼 스크롤 상자가 보여 주는 범위 밖으로는 띄우지 않는다 — 커서를 표에 둔 채 스크롤해 표가 밀려
 *   나가면, 몸통에 띄운 단추가 남의 머리글 위에 남아 눌린다(2차 검토 지적 2026-10-06). 위가 잘리면 아래로,
 *   아래도 잘리면 숨긴다.
 * 층: 표 손잡이와 같은 9999982(styles/z-index.css --z-editor-table-handle — 그 변수는 로드되지 않아 숫자로 쓴다).
 * 떠 있는 자리: document.body 에 포털 — 전역 preserve-3d 때문에 남의 상자 안 absolute 는 기준이 흔들린다.
 */
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $isTableNode, TableNode } from '@lexical/table';
import { $findMatchingParent, mergeRegister } from '@lexical/utils';
import {
  $getNodeByKey,
  $getSelection,
  $isRangeSelection,
  DEPRECATED_$isGridSelection,
  LexicalEditor,
  NodeKey,
} from 'lexical';
import { AlignCenter, AlignLeft, AlignRight, LucideIcon } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import {
  containerContentWidth,
  TableAlign,
  tableAlignOf,
} from '../utils/tableWidth';
import { $setTableAlign } from '../utils/tableWidthLexical';

export const FLOAT_BUTTON_PX = 24;
const GAP_ABOVE_PX = 4;
const GAP_BELOW_PX = 12;
const FLOAT_Z = 9999982;

type Rect = { left: number; right: number; top: number; bottom: number };
type Spot = { x: number; y: number };
export type FloatSpots = {
  placement: 'above' | 'below';
  left: Spot;
  center: Spot;
  right: Spot;
};

/** 단추 자리(화면 좌표). limitTop = 편집기 글자 칸의 위쪽 끝 — 그 위로는 올리지 않는다 */
export function floatButtonSpots(table: Rect, limitTop: number): FloatSpots {
  const size = FLOAT_BUTTON_PX;
  const above = table.top - GAP_ABOVE_PX - size >= limitTop;
  const y = above ? table.top - GAP_ABOVE_PX - size : table.bottom + GAP_BELOW_PX;
  const placement = above ? 'above' : 'below';
  const width = table.right - table.left;
  const row = size * 3 + GAP_ABOVE_PX * 2;
  if (width < row) {
    const start = table.left + width / 2 - row / 2;
    const step = size + GAP_ABOVE_PX;
    return {
      placement,
      left: { x: start, y },
      center: { x: start + step, y },
      right: { x: start + step * 2, y },
    };
  }
  return {
    placement,
    left: { x: table.left, y },
    center: { x: table.left + width / 2 - size / 2, y },
    right: { x: table.right - size, y },
  };
}

type FloatState = { tableKey: NodeKey; align: TableAlign; spots: FloatSpots };

/** 표를 감싼 스크롤 상자들이 실제로 보여 주는 세로 범위(화면 좌표) */
function visibleRangeOf(el: HTMLElement): { top: number; bottom: number } {
  let top = 0;
  let bottom = window.innerHeight;
  for (let p = el.parentElement; p && p !== document.body; p = p.parentElement) {
    const overflowY = window.getComputedStyle(p).overflowY;
    if (overflowY !== 'visible') {
      const r = p.getBoundingClientRect();
      top = Math.max(top, r.top);
      bottom = Math.min(bottom, r.bottom);
    }
  }
  return { top, bottom };
}

function readFloatState(editor: LexicalEditor): FloatState | null {
  return editor.getEditorState().read(() => {
    if (!editor.isEditable()) return null;
    const root = editor.getRootElement();
    if (!root) return null;
    const selection = $getSelection();
    let tableNode: TableNode | null = null;
    if ($isRangeSelection(selection)) {
      const native = window.getSelection();
      if (!native || !native.anchorNode || !root.contains(native.anchorNode)) return null;
      const found = $findMatchingParent(selection.anchor.getNode(), $isTableNode);
      if ($isTableNode(found)) tableNode = found;
    } else if (DEPRECATED_$isGridSelection(selection)) {
      if (!root.contains(document.activeElement)) return null;
      const found = $getNodeByKey(selection.gridKey);
      if ($isTableNode(found)) tableNode = found;
    }
    if (!tableNode) return null;
    const tableEl = editor.getElementByKey(tableNode.getKey());
    if (!tableEl) return null;
    const rect = tableEl.getBoundingClientRect();
    if (rect.width >= containerContentWidth(tableEl) - 1) return null;
    const visible = visibleRangeOf(tableEl);
    const spots = floatButtonSpots(rect, Math.max(root.getBoundingClientRect().top, visible.top));
    if (spots.left.y < visible.top || spots.left.y + FLOAT_BUTTON_PX > visible.bottom) return null;
    return {
      tableKey: tableNode.getKey(),
      align: tableAlignOf(tableNode.getFormatType()),
      spots,
    };
  });
}

const sameState = (a: FloatState | null, b: FloatState | null) =>
  a === b || (!!a && !!b && JSON.stringify(a) === JSON.stringify(b));

const BUTTONS: { align: TableAlign; label: string; Icon: LucideIcon }[] = [
  { align: 'left', label: '표 왼쪽 정렬', Icon: AlignLeft },
  { align: 'center', label: '표 가운데 정렬', Icon: AlignCenter },
  { align: 'right', label: '표 오른쪽 정렬', Icon: AlignRight },
];

export default function TableAlignFloat(): JSX.Element | null {
  const [editor] = useLexicalComposerContext();
  const [state, setState] = useState<FloatState | null>(null);

  useEffect(() => {
    let frame = 0;
    // 그린 뒤에 잰다 — 표 폭 입히기(TableWidthPlugin)가 같은 갱신 안에서 표 크기를 바꾼다
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const next = readFloatState(editor);
        setState((prev) => (sameState(prev, next) ? prev : next));
      });
    };
    schedule();
    document.addEventListener('selectionchange', schedule);
    document.addEventListener('scroll', schedule, true);
    window.addEventListener('resize', schedule);
    const unregister = mergeRegister(
      editor.registerUpdateListener(() => schedule()),
      editor.registerEditableListener(() => schedule())
    );
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('selectionchange', schedule);
      document.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', schedule);
      unregister();
    };
  }, [editor]);

  const pick = useCallback(
    (align: TableAlign) => {
      if (!state) return;
      const key = state.tableKey;
      editor.update(() => {
        const node = $getNodeByKey(key);
        if ($isTableNode(node)) $setTableAlign(node, align);
      });
    },
    [editor, state]
  );

  if (!state) return null;
  const scrollX = window.scrollX;
  const scrollY = window.scrollY;
  return createPortal(
    <div style={{ position: 'absolute', left: 0, top: 0, zIndex: FLOAT_Z }}>
      {BUTTONS.map(({ align, label, Icon }) => {
        const spot = state.spots[align];
        const active = state.align === align;
        return (
          <button
            key={align}
            type="button"
            title={label}
            aria-label={label}
            aria-pressed={active}
            className={`absolute flex size-6 items-center justify-center rounded-lg shadow ${
              active
                ? 'bg-primary text-white hover:bg-primary-hover'
                : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-100'
            }`}
            style={{ left: spot.x + scrollX, top: spot.y + scrollY }}
            // 커서를 표 안에 남긴다 — 빠지면 단추가 사라지고 다음 정렬을 바로 못 누른다
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => pick(align)}
          >
            <Icon size={14} strokeWidth={2.25} aria-hidden />
          </button>
        );
      })}
    </div>,
    document.body
  );
}
